import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import EstablishmentList from '@/components/EstablishmentList';

export default function AdminEstablishmentsPage() {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const pageSize = 50;

  const establishmentsQuery = useQuery({
    queryKey: ['admin:establishments', page],
    queryFn: () => apiClient.getAdminEstablishments(page * pageSize, pageSize),
  });

  const establishments = establishmentsQuery.data?.data || [];
  const total = establishmentsQuery.data?.total || 0;

  const filteredEstablishments = searchTerm
    ? establishments.filter((e: any) =>
        e.name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : establishments;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900">🏢 {t('admin.establishmentsTitle')}</h1>
        <p className="text-neutral-600 mt-2">{t('admin.establishmentsSubtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
          <p className="text-purple-100 text-sm font-medium">{t('admin.totalEstablishments')}</p>
          <p className="text-3xl font-bold mt-2">{total}</p>
        </div>
        <div className="bg-white rounded-xl p-6 border border-neutral-200 shadow-sm">
          <p className="text-neutral-600 text-sm font-medium">{t('admin.thisPage')}</p>
          <p className="text-3xl font-bold text-neutral-900 mt-2">{establishments.length}</p>
        </div>
        <div className="bg-white rounded-xl p-6 border border-neutral-200 shadow-sm">
          <p className="text-neutral-600 text-sm font-medium">{t('admin.filtered')}</p>
          <p className="text-3xl font-bold text-neutral-900 mt-2">{filteredEstablishments.length}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder={t('admin.searchByName')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="w-full sm:w-auto px-4 py-2 text-sm text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              {t('admin.clear')}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h2 className="font-semibold text-neutral-900">{t('admin.establishmentsTitle')}</h2>
          <span className="text-sm text-neutral-500">{t('admin.results', { count: filteredEstablishments.length })}</span>
        </div>
        <EstablishmentList
          establishments={filteredEstablishments}
          isLoading={establishmentsQuery.isLoading}
        />
      </div>

      {total > pageSize && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-neutral-600">
            {t('admin.showingPage', {
              from: page * pageSize + 1,
              to: Math.min((page + 1) * pageSize, total),
              total,
            })}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-neutral-200 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('admin.previous')}
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * pageSize >= total}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-neutral-200 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('admin.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
